import React from "react";
import type { ActiveWorkflow, WorkflowCatalogEntry } from "../types/workflow";
import { formatRelativeTime } from "../utils/formatRelativeTime";
import { workflowLabel } from "./CatalogCard";
import { NEEDS_YOU_LABEL, statusDisplayLabel } from "@product-app/common/workflows/statusLabels";
import {
  isNeedsYouStatus,
  isWaitingOnPerson,
  type WaitSignals,
} from "@product-app/common/workflows/statusPill";

/**
 * `signals` (optional): who a parked run waits on beyond its status — a
 * `pending_edocs` run waiting for the Servicer's OWN documents reads like a
 * `paused_hitl` one (story 28633, `isWaitingOnPerson`). Absent: the status alone.
 */
function waitingBySignals(status: string, signals?: WaitSignals): boolean {
  return isWaitingOnPerson(status, signals) && !isNeedsYouStatus(status);
}

export function statusTone(status: string, signals?: WaitSignals): string {
  if (status === "completed") return "text-primary-success-600";
  if (status === "failed") return "text-primary-error-600";
  if (status.includes("hitl") || status.includes("waiting") || waitingBySignals(status, signals))
    return "text-primary-warning-500";
  return "text-blossom";
}

export function statusDotBg(status: string, signals?: WaitSignals): string {
  if (status === "completed") return "bg-primary-success-600";
  if (status === "failed") return "bg-primary-error-600";
  if (status.includes("hitl") || status.includes("waiting") || waitingBySignals(status, signals))
    return "bg-primary-warning-500";
  return "bg-sunrise";
}

export function statusLabel(status: string, signals?: WaitSignals): string {
  return isWaitingOnPerson(status, signals) ? NEEDS_YOU_LABEL : statusDisplayLabel(status);
}

/**
 * HITL question kinds that resolve on their own — the run is queued/waiting,
 * not asking the user anything. Rendered as a calm "Waiting..." row with NO
 * Respond button ("Input Needed" + a pulsing CTA for a wait the user cannot
 * act on reads as a broken workflow).
 */
const AUTO_RESUME_HITL_KINDS = new Set(["waiting_for_service_principal"]);

export function isAutoResumeWait(wf: Pick<ActiveWorkflow, "hitl_kind">): boolean {
  return !!wf.hitl_kind && AUTO_RESUME_HITL_KINDS.has(wf.hitl_kind);
}

/** Short card label for a known auto-resume wait kind. */
export function autoResumeWaitLabel(hitlKind: string | null | undefined): string {
  if (hitlKind === "waiting_for_service_principal") return "Waiting for Epic account";
  return "Waiting";
}

export interface ActiveWorkflowCardProps {
  wf: ActiveWorkflow;
  catalogEntry?: WorkflowCatalogEntry;
  isActive?: boolean;
  onClick: () => void;
  onRespond?: () => void;
}

export const ActiveWorkflowCard: React.FC<ActiveWorkflowCardProps> = ({
  wf,
  catalogEntry,
  isActive,
  onClick,
  onRespond,
}) => {
  const label = catalogEntry?.name ?? workflowLabel(wf.workflow_id);
  // Auto-resume waits (queued for a shared Epic account) are paused_hitl at
  // the substrate level but require NO response — suppress the Respond CTA
  // and swap the "Input Needed" chip for a calm waiting label.
  const autoWait = isAutoResumeWait(wf);
  const isPausedHitl =
    !autoWait && (wf.status === "paused_hitl" || wf.status === "warm_hitl");
  const signals: WaitSignals = { needsYou: wf.needs_you };

  return (
    <div
      className={
        "w-full rounded-[10px] border border-primary-warning-300 bg-primary-warning-50 transition-shadow duration-200 hover:bg-primary-warning-100 hover:shadow-sm " +
        (isActive ? "ring-2 ring-sunrise ring-offset-1" : "")
      }
    >
      <button
        type="button"
        onClick={onClick}
        data-testid={`inflight-card-${wf.instance_id}`}
        className="w-full px-3 py-[10px] text-left active:scale-[0.99]"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-[6px]">
              <span className="text-sm font-semibold text-primary-gray-light-900">
                {wf.name ?? "Unassigned"}
              </span>
              {isActive && (
                <span className="rounded-full bg-sunrise px-[6px] py-[1px] text-2xs font-bold uppercase tracking-[0.04em] text-primary-gray-light-900">
                  Open
                </span>
              )}
            </div>
            <div className="mt-[2px] truncate text-xs text-primary-gray-light-500">
              {label}
            </div>
            <div className="mt-1 flex items-center gap-[5px] text-xs text-primary-gray-light-400">
              <span>{formatRelativeTime(wf.created_at)}</span>
              {wf.current_phase && (
                <span>· {wf.current_phase.replace(/_/g, " ")}</span>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-[5px] self-center">
            <div
              className={
                "h-[6px] w-[6px] rounded-full " +
                (autoWait ? "bg-primary-gray-light-400" : statusDotBg(wf.status, signals)) +
                (wf.status === "running" ? " motion-safe:animate-running-pulse" : "")
              }
            />
            <span
              className={
                "text-xs font-medium " +
                (autoWait ? "text-primary-gray-light-500" : statusTone(wf.status, signals))
              }
            >
              {autoWait ? autoResumeWaitLabel(wf.hitl_kind) : statusLabel(wf.status, signals)}
            </span>
          </div>
        </div>
      </button>

      {isPausedHitl && onRespond && (
        <div className="px-3 pb-[10px]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRespond();
            }}
            className="w-full rounded-md bg-sunrise px-3 py-1.5 text-xs font-semibold text-primary-gray-light-900 transition-colors duration-200 hover:bg-blossom motion-safe:animate-pulse"
            data-testid={`respond-${wf.instance_id}`}
          >
            Respond
          </button>
        </div>
      )}
    </div>
  );
};

export default ActiveWorkflowCard;
